import React, { useState, useEffect, useRef } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import axios from 'axios';
import './AdminRawThoughtForm.css';

const AdminRawThoughtForm = ({ thought, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category: '',
        tags: '',
        attachments: []
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [interimText, setInterimText] = useState('');
    const fileInputRef = useRef(null);
    const audioRef = useRef(null);
    const recognitionRef = useRef(null);

    useEffect(() => {
        if (thought) {
            let parsedAttachments = [];
            if (thought.attachments) {
                try {
                    parsedAttachments = JSON.parse(thought.attachments);
                } catch (error) {
                    console.warn('Failed to parse attachments JSON:', error);
                    parsedAttachments = [];
                }
            }
            
            setFormData({
                title: thought.title || '',
                content: thought.content || '',
                category: thought.category || '',
                tags: thought.tags || '',
                attachments: parsedAttachments
            });
        }
    }, [thought]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleContentChange = (event, editor) => {
        const data = editor.getData();
        setFormData(prev => ({ ...prev, content: data }));
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setUploadingFile(true);
        setError(null);

        try {
            const token = localStorage.getItem('app_token');
            const uploadPromises = files.map(async (file) => {
                const formData = new FormData();
                formData.append('file', file);

                const response = await axios.post('/api/raw-thoughts/upload-attachment', formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                return {
                    url: response.data.url,
                    filename: response.data.filename,
                    size: response.data.size,
                    type: response.data.type
                };
            });

            const uploadedFiles = await Promise.all(uploadPromises);
            setFormData(prev => ({
                ...prev,
                attachments: [...prev.attachments, ...uploadedFiles]
            }));
        } catch (err) {
            setError('Failed to upload file(s)');
            console.error('Error uploading files:', err);
        } finally {
            setUploadingFile(false);
        }
    };

    const removeAttachment = (index) => {
        setFormData(prev => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index)
        }));
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            const chunks = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            recorder.onstop = () => {
                const blob = new Blob(chunks, { type: 'audio/wav' });
                setAudioChunks(chunks);
                if (audioRef.current) {
                    audioRef.current.src = URL.createObjectURL(blob);
                }
            };

            recorder.start();
            setMediaRecorder(recorder);
            setIsRecording(true);
        } catch (err) {
            setError('Failed to start recording. Please check microphone permissions.');
            console.error('Error starting recording:', err);
        }
    };

    const stopRecording = () => {
        if (mediaRecorder) {
            mediaRecorder.stop();
            mediaRecorder.stream.getTracks().forEach(track => track.stop());
            setIsRecording(false);
        }
    };

    const transcribeAudio = async () => {
        if (audioChunks.length === 0) return;

        setTranscribing(true);
        setError(null);

        try {
            const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
            const formData = new FormData();
            formData.append('file', audioBlob, 'recording.wav');

            const token = localStorage.getItem('app_token');
            const response = await axios.post('/api/raw-thoughts/transcribe-audio', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Add transcribed text to content
            setFormData(prev => {
                const prevContent = (prev.content || '')
                    .replace(/[\n\r]+$/g, '')
                    .replace(/(<p><br><\/p>|<p><\/p>)+$/g, '')
                    .replace(/\s+$/g, '');
                const newText = replaceSpokenPunctuation(response.data.text).replace(/^[\n\r\s]+/g, '');
                return {
                    ...prev,
                    content: prevContent + newText
                };
            });

            // Clear audio
            setAudioChunks([]);
            if (audioRef.current) {
                audioRef.current.src = '';
            }
        } catch (err) {
            setError('Failed to transcribe audio');
            console.error('Error transcribing audio:', err);
        } finally {
            setTranscribing(false);
        }
    };

    function replaceSpokenPunctuation(text) {
        return text
            .replace(/ full stop/gi, '.')
            .replace(/ comma/gi, ',')
            .replace(/ question mark/gi, '?')
            .replace(/ exclamation mark/gi, '!')
            .replace(/ new line/gi, '\n')
            .replace(/ colon/gi, ':')
            .replace(/ semicolon/gi, ';')
            .replace(/ dash/gi, '-')
            .replace(/ open bracket/gi, '(')
            .replace(/ close bracket/gi, ')');
    }

    // Live speech-to-text handlers
    const handleStartListening = () => {
        if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
            setError('Speech recognition is not supported in this browser.');
            return;
        }
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.lang = 'en-US';
        recognition.interimResults = true;
        recognition.continuous = true;
        recognition.onresult = (event) => {
            let interimTranscript = '';
            let finalTranscript = '';
            for (let i = event.resultIndex; i < event.results.length; ++i) {
                if (event.results[i].isFinal) {
                    finalTranscript += event.results[i][0].transcript;
                } else {
                    interimTranscript += event.results[i][0].transcript;
                }
            }
            // Only append final transcript to content, with spoken punctuation replaced
            if (finalTranscript) {
                setFormData(prev => {
                    const prevContent = (prev.content || '')
                        .replace(/[\n\r]+$/g, '')
                        .replace(/(<p><br><\/p>|<p><\/p>)+$/g, '')
                        .replace(/\s+$/g, '');
                    const newText = replaceSpokenPunctuation(finalTranscript).replace(/^[\n\r\s]+/g, '');
                    return {
                        ...prev,
                        content: prevContent + newText
                    };
                });
            }
            // Show interim transcript as a live preview, with spoken punctuation replaced
            setInterimText(replaceSpokenPunctuation(interimTranscript));
        };
        recognition.onerror = (e) => {
            setError('Speech recognition error: ' + e.error);
            setIsListening(false);
            setInterimText('');
        };
        recognition.onend = () => {
            setIsListening(false);
            setInterimText('');
        };
        recognitionRef.current = recognition;
        recognition.start();
        setIsListening(true);
    };

    const handleStopListening = () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            setIsListening(false);
            setInterimText('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            await onSave(formData);
        } catch (err) {
            setError(err.response?.data?.error || 'An unexpected error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="raw-thought-form-overlay">
            <div className="raw-thought-form-modal">
                <div className="form-header">
                    <h2>{thought ? 'Edit Raw Thought' : 'Create New Raw Thought'}</h2>
                    <button onClick={onCancel} className="close-btn">
                        <span className="material-icons">close</span>
                    </button>
                </div>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit} className="raw-thought-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="title">Title *</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                placeholder="Enter thought title..."
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="category">Category</label>
                            <input
                                type="text"
                                id="category"
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                placeholder="Enter category..."
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="tags">Tags</label>
                            <input
                                type="text"
                                id="tags"
                                name="tags"
                                value={formData.tags}
                                onChange={handleChange}
                                placeholder="Enter tags (comma-separated)..."
                            />
                        </div>
                    </div>

                    {/* File Upload Section */}
                    <div className="form-group">
                        <label>Attachments</label>
                        <div className="file-upload-section">
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="btn-secondary"
                                disabled={uploadingFile}
                            >
                                <span className="material-icons">attach_file</span>
                                {uploadingFile ? 'Uploading...' : 'Upload Files'}
                            </button>
                        </div>

                        {/* Attachments List */}
                        {formData.attachments.length > 0 && (
                            <div className="attachments-list">
                                {formData.attachments.map((attachment, index) => (
                                    <div key={index} className="attachment-item">
                                        <div className="attachment-info">
                                            <span className="material-icons">
                                                {attachment.type.startsWith('image/') ? 'image' :
                                                 attachment.type.startsWith('video/') ? 'video_file' :
                                                 attachment.type.startsWith('audio/') ? 'audiotrack' :
                                                 'description'}
                                            </span>
                                            <span className="attachment-name">{attachment.filename}</span>
                                            <span className="attachment-size">{formatFileSize(attachment.size)}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeAttachment(index)}
                                            className="remove-attachment"
                                        >
                                            <span className="material-icons">close</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Content Editor with Mic Button */}
                    <div className="form-group" style={{ position: 'relative' }}>
                        <label>Content *</label>
                        <CKEditor
                            editor={ClassicEditor}
                            data={formData.content}
                            onChange={handleContentChange}
                            config={{
                                toolbar: [
                                    'heading', '|',
                                    'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                                    'outdent', 'indent', '|',
                                    'blockQuote', 'insertTable', 'undo', 'redo'
                                ]
                            }}
                        />
                        {isListening && interimText && (
                            <div style={{ marginTop: 8, color: '#64748b', fontStyle: 'italic', minHeight: 24 }}>
                                {interimText}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={isListening ? handleStopListening : handleStartListening}
                            className="mic-btn"
                            style={{
                                position: 'absolute',
                                top: 32,
                                right: 8,
                                background: isListening ? '#fee2e2' : '#e0e7ff',
                                border: 'none',
                                borderRadius: '50%',
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: isListening ? '0 0 0 2px #ef4444' : 'none',
                            }}
                            title={isListening ? 'Stop Listening' : 'Speak to transcribe'}
                        >
                            <span className="material-icons" style={{ color: isListening ? '#ef4444' : '#2563eb', fontSize: 24 }}>
                                {isListening ? 'mic_off' : 'mic'}
                            </span>
                        </button>
                    </div>

                    <div className="form-actions">
                        <button type="button" onClick={onCancel} className="btn-secondary" disabled={isLoading}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={isLoading}>
                            {isLoading ? 'Saving...' : (thought ? 'Update Thought' : 'Create Thought')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminRawThoughtForm; 